import decode from 'jwt-decode'
import React, { useEffect } from 'react'
import { Logger } from 'typescript-log'
import { BaseClientConfig } from 'common/client/BaseClientConfig'
import {
    exchangeRefreshTokens,
    persistAuthTokens,
    retrieveAuthTokens,
    getAuthStateFromAccessToken,
    AuthTokenPayload,
    clearAuthTokens,
} from 'common/authentication/helpers'
import { useDispatch, useStore } from 'react-redux'
import { Dispatch } from 'redux'
import { withRouter, RouteComponentProps } from 'react-router'
import {
    authLogin,
    authLogout,
    authEntitlementsChecked,
    authRefreshLogin,
    AuthenticationState,
    authSessionExpired,
    authNetworkError,
} from 'common/data/authentication/reducer'
import { useToggleState } from 'common/feature-togglings/features'
import { UserAuthEvents } from 'common/events/user-event-types'
import { AuthenticationError } from 'common/authentication/errors'

export interface AuthenticationHandlerProps {
    logger: Logger
    config: BaseClientConfig
    onEvent: (authEvent: UserAuthEvents) => void
}

const Authentication: React.SFC<RouteComponentProps<{}> & AuthenticationHandlerProps> = ({
    history,
    config,
    logger,
    onEvent,
}) => {
    const dispatch = useDispatch()
    const toggleState = useToggleState()
    const store = useStore()

    useEffect(() => {
        checkAuthenticationStatus(dispatch, onEvent)
    }, [config, logger, dispatch, toggleState, onEvent])

    useEffect(() => {
        const authenticationState = store.getState().authentication
        checkAndRefreshTokenState(logger, config, dispatch, authenticationState, onEvent)
    }, [history.location.pathname, logger, config, dispatch, store, onEvent])

    return null
}

export const AuthenticationHandler = withRouter(Authentication)

export const checkAuthenticationStatus = async (
    dispatch: Dispatch<any>,
    onEvent: (authEvent: UserAuthEvents) => void
) => {
    const tokenState = retrieveAuthTokens()
    if (tokenState) {
        const authState = getAuthStateFromAccessToken(tokenState.accessToken, onEvent)

        if (authState) {
            dispatch(authLogin(authState))
        }
    }
    dispatch(authEntitlementsChecked())
}

export const checkAndRefreshTokenState = async (
    logger: Logger,
    config: BaseClientConfig,
    dispatch: Dispatch<any>,
    authenticationState: AuthenticationState,
    onEvent: (authEvent: UserAuthEvents) => void
) => {
    const tokenState = retrieveAuthTokens()

    if (!tokenState) {
        logger.info('No token cookie found, logging out')
        authenticationState.isLoggedIn && dispatch(authLogout({ onEvent }))
        return
    }

    // we do not yet validate the JTW, we can do that with
    // https://github.com/cisco/node-jose or https://github.com/square/js-jose
    const payload = decode(tokenState.accessToken) as AuthTokenPayload

    const isTokenExpired = payload.exp <= new Date().getTime() / 1000

    dispatch(authEntitlementsChecked())

    if (!isTokenExpired) {
        logger.debug('Access token not expired')
        const authState = getAuthStateFromAccessToken(tokenState.accessToken, onEvent)
        dispatch(authRefreshLogin(authState))
        return
    }

    try {
        logger.debug({ sub: payload.sub }, 'access_token has expired, refreshing...')
        const exchangeRefreshTokensResponse = await exchangeRefreshTokens(
            config,
            logger,
            tokenState.refreshToken
        )

        const authState = getAuthStateFromAccessToken(
            exchangeRefreshTokensResponse.access_token,
            onEvent
        )

        logger.debug('Refreshing login state with new access token')
        persistAuthTokens(exchangeRefreshTokensResponse)
        dispatch(authRefreshLogin(authState))
    } catch (error) {
        // When the authentication server responds with a 400 error we've now entered a state
        // we can't recover from. The users session will be expired and the stale cookies cleared.
        if (error instanceof AuthenticationError && error.response.status === 400) {
            logger.error(
                {
                    sub: payload.sub,
                    isLoggedIn: authenticationState.isLoggedIn,
                    err: error,
                },
                'Issue refreshing tokens'
            )
            dispatch(authSessionExpired({ onEvent }))
            clearAuthTokens()
            return
        }
        // Recovery should be possible in this error state. Flag a network error
        // on the next refresh attempt try again.
        dispatch(authNetworkError({ onEvent }))
        return
    }
}
